/**
 * Help-centre article parser — the SINGLE implementation of the article contract.
 *
 * Deliberately plain ESM with no imports and no filesystem access, because it has two very
 * different callers:
 *
 *   1. `src/content/help/index.ts` — bundled into the app, feeds it raw strings collected
 *      by `import.meta.glob`.
 *   2. `scripts/help/validate.mjs` — runs in Node during `vite build` (and on `vite dev`)
 *      via the plugin in `vite.config.ts`, feeds it strings read from disk.
 *
 * ⚠️ Why this split exists at all: `import.meta.glob` inlines file CONTENT at build time,
 * but the module's validation code only EXECUTES in the browser. Validation living only in
 * `index.ts` therefore turns a malformed article into a white screen for the reader instead
 * of a failed build — which is exactly the bug this file was created to fix. Keep the rules
 * here, where the Node-side validator can run them.
 *
 * Ported from the marketing site (`code/websites/xoe/scripts/docs/parse.mjs`) when the help
 * centre moved in-app (ClickUp 86d3zt9d3); the case-study half stayed behind.
 *
 * Types for the TS caller live in `parse.d.mts`.
 */

const DOC_TYPES = ["how-to", "reference", "explanation", "tutorial"];
// `patient` guides live in the `patient-portal` category so practitioners can find and
// share them, but the audience field keeps them distinguishable from staff-facing content —
// it is what the in-app role filter keys on.
const DOC_AUDIENCES = ["practitioner", "patient"];

/**
 * Heading anchor slug. PURE — no de-duplication counter.
 *
 * A stateful slugger would be wrong: React can re-render the same tree, and a counter that
 * survives a re-render starts emitting `-2`/`-3` suffixes for headings that are not
 * duplicates, breaking every anchor on the page. Instead `extractHeadings` fails the build
 * when one article has two headings that slugify identically, so the author renames one and
 * every anchor stays stable.
 */
export function slugify(text) {
  return (
    String(text)
      .toLowerCase()
      .replace(/[`*_~]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-{2,}/g, "-") || "section"
  );
}

function unquote(value) {
  const trimmed = String(value).trim();
  if (trimmed.length >= 2) {
    const first = trimmed[0];
    const last = trimmed[trimmed.length - 1];
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return trimmed.slice(1, -1);
    }
  }
  return trimmed;
}

/**
 * Minimal front-matter reader — `key: value` and `key: [a, b]` only.
 *
 * Deliberately not a YAML parser. The article contract is a flat set of scalars plus one
 * string array, so a dependency buys nothing and brings YAML's surprises around `no`, `1.0`
 * and bare dates. Anything it cannot read is an error, never a silent skip.
 */
function parseFrontMatter(raw, file) {
  const match = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/.exec(raw);
  if (!match) {
    throw new Error(`${file}: missing front matter (the file must start with a --- block)`);
  }

  const data = {};
  for (const line of match[1].split(/\r?\n/)) {
    if (!line.trim() || line.trimStart().startsWith("#")) continue;

    const idx = line.indexOf(":");
    if (idx === -1) {
      throw new Error(`${file}: malformed front-matter line (expected "key: value") — ${line}`);
    }

    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();

    if (value.startsWith("[") && value.endsWith("]")) {
      const inner = value.slice(1, -1).trim();
      data[key] = inner ? inner.split(",").map(unquote).filter(Boolean) : [];
    } else {
      data[key] = unquote(value);
    }
  }

  return { data, body: raw.slice(match[0].length) };
}

function requireString(data, key, file) {
  const value = data[key];
  if (typeof value !== "string" || !value) {
    throw new Error(`${file}: front matter is missing required key "${key}"`);
  }
  return value;
}

/** h2/h3 headings in document order, skipping anything inside a fenced code block. */
function extractHeadings(body, file) {
  const headings = [];
  const seen = new Set();
  let inFence = false;

  for (const line of body.split(/\r?\n/)) {
    if (/^\s{0,3}(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!match) continue;

    const text = match[2].replace(/[`*_]/g, "").trim();
    const id = slugify(text);
    if (seen.has(id)) {
      throw new Error(
        `${file}: two headings both produce the anchor "#${id}" ("${text}"). Rename one — ` +
          `duplicate anchors make the table of contents link to the wrong section.`,
      );
    }
    seen.add(id);
    headings.push({ depth: match[1].length, text, id });
  }

  return headings;
}

/**
 * Parse and validate one article.
 *
 * @param file  Path relative to `src/content/help`, e.g. `getting-started/log-in.md`.
 * @param raw   Full file contents including front matter.
 * @param knownCategorySlugs  Category slugs declared in `categories.json`.
 * @throws on any contract violation.
 */
export function parseArticle(file, raw, knownCategorySlugs) {
  const clean = file.replace(/^\.\//, "");
  const { data, body } = parseFrontMatter(raw, clean);

  const parts = clean.replace(/\.md$/, "").split("/");
  if (parts.length !== 2) {
    throw new Error(
      `${clean}: articles must live exactly one directory deep — src/content/help/<category>/<slug>.md`,
    );
  }
  const [dirCategory, fileSlug] = parts;

  const title = requireString(data, "title", clean);
  const slug = requireString(data, "slug", clean);
  const category = requireString(data, "category", clean);
  const type = requireString(data, "type", clean);
  const audience = requireString(data, "audience", clean);
  const summary = requireString(data, "summary", clean);
  const updated = requireString(data, "updated", clean);
  const orderRaw = requireString(data, "order", clean);

  if (slug !== fileSlug) {
    throw new Error(`${clean}: front-matter slug "${slug}" does not match the filename "${fileSlug}"`);
  }
  if (category !== dirCategory) {
    throw new Error(`${clean}: front-matter category "${category}" does not match the directory "${dirCategory}"`);
  }
  if (!knownCategorySlugs.includes(category)) {
    throw new Error(
      `${clean}: unknown category "${category}". Declare it in src/content/help/categories.json first.`,
    );
  }
  if (!DOC_TYPES.includes(type)) {
    throw new Error(`${clean}: type "${type}" must be one of ${DOC_TYPES.join(" | ")}`);
  }
  if (!DOC_AUDIENCES.includes(audience)) {
    throw new Error(`${clean}: audience "${audience}" must be one of ${DOC_AUDIENCES.join(" | ")}`);
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(updated)) {
    throw new Error(`${clean}: updated "${updated}" must be an ISO date (YYYY-MM-DD)`);
  }

  const order = Number(orderRaw);
  if (!Number.isFinite(order)) {
    throw new Error(`${clean}: order "${orderRaw}" must be a number`);
  }
  if (!body.trim()) {
    throw new Error(`${clean}: article body is empty`);
  }

  const relatedRaw = data.related;
  const related = Array.isArray(relatedRaw) ? relatedRaw : relatedRaw ? [relatedRaw] : [];

  const captureSpec =
    typeof data.captureSpec === "string" && data.captureSpec ? data.captureSpec : undefined;

  return {
    title,
    slug,
    category,
    type,
    audience,
    order,
    summary,
    related,
    captureSpec,
    updated,
    body,
    path: `/help/guides/${category}/${slug}`,
    headings: extractHeadings(body, clean),
  };
}

/**
 * Cross-article checks that need the whole set: duplicate routes and dangling `related:`
 * slugs. Returns an array of error strings (empty when clean).
 */
export function crossCheck(articles) {
  const errors = [];
  const seen = new Set();

  for (const article of articles) {
    if (seen.has(article.path)) errors.push(`duplicate article path "${article.path}"`);
    seen.add(article.path);
  }

  const slugs = new Set(articles.map((a) => a.slug));
  for (const article of articles) {
    for (const slug of article.related) {
      if (!slugs.has(slug)) {
        errors.push(`${article.category}/${article.slug}: related article "${slug}" does not exist`);
      }
    }
  }

  return errors;
}
